import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as CorgeeHelper from '@components/utils/CorgeeHelper';
import $ from 'jquery';
import ReplyApi from './api/ReplyApi';
import MenuApi from './api/MenuApi';
// ConversationApi and ClickOutSide imports removed (unused)
import threeDotsSvg from '@/assets/icons/three-dots.svg';
import SubmitTime from '@components/SubmitTime/SubmitTime';
import { conversationBus } from '@/lib/conversationBus';

// Types
interface MenuItem {
  ConfigValueID: number;
  Text: string;
  Icon: string;
}

interface Reply {
  ReplyId: number;
  ConversationId: number;
  IsImportant: boolean;
  CreatedById: number;
  CreatedAt: string;
  SocketConnectionId?: string;
}

interface Conversation {
  ConversationId: number;
  ProjectId: number;
  ConfigValueId: number;
}

interface ApiConfig {
  url: string;
  [key: string]: any;
}

interface ConfigMenus {
  reply?: string[];
}

interface CorgeeFunctions {
  editReply?: (e: React.MouseEvent, reply: Reply, conversation: Conversation) => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  onReplyDeleted?: (e: React.MouseEvent, reply: Reply, totalReplies: number) => void;
  onCreateItemFromCustomType?: (item: MenuItem, conversation: Conversation) => void;
}

interface ActionMenuReplyProps {
  reply: Reply;
  conversation: Conversation;
  api: ApiConfig;
  isImportant?: boolean;
  closeOnResizeWindow?: boolean;
  onDeleteReply?: (
    conversationId: number,
    replyId: number,
    callback: (totalReplies: number) => void
  ) => void;
  onAddRepost?: (reply: Reply) => void;
  onMarkAsImportant?: () => void;
  onSubmitTime?: (conversation?: any) => void;
  onAddReply?: (
    conversationId: number,
    reply: any,
    sendUpdate?: boolean,
    updateTotalReplies?: boolean
  ) => void;
  SocketConnectionId?: string;
  configMenus?: ConfigMenus;
  isInfowindowViewCard?: boolean;
  corgeeFunctions?: CorgeeFunctions;
}

const ActionMenuReply: React.FC<ActionMenuReplyProps> = ({
  reply,
  conversation,
  api,
  isImportant,
  closeOnResizeWindow = false,
  onDeleteReply,
  onAddRepost,
  onMarkAsImportant,
  onSubmitTime,
  onAddReply,
  SocketConnectionId,
  configMenus,
  isInfowindowViewCard,
  corgeeFunctions,
}) =>
{
  // Refs
  const actionMenuElementRef = useRef<HTMLDivElement>(null);
  const corgeeSubMenuItemRef = useRef<HTMLUListElement>(null);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canTemporary, setCanTemporary] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [subMenuItems, setSubMenuItems] = useState<MenuItem[]>([]);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [submitTimeOpen, setSubmitTimeOpen] = useState(false);

  // Handle scroll
  const handleScroll = useCallback(() =>
  {
    if (
      actionMenuElementRef.current &&
      $(actionMenuElementRef.current).hasClass('open') &&
      !isInfowindowViewCard
    )
    {
      $(actionMenuElementRef.current).removeClass('open');
      setIsOpen(false);
    }
  }, [isInfowindowViewCard]);

  // On resize window
  const onResizeWindow = useCallback((e: Event) =>
  {
    e.preventDefault();
    setIsOpen(false);
  }, []);

  // Close menu
  const closeMenu = useCallback(() =>
  {
    setIsOpen(false);
    setIsPositioned(false);
    setShowSubMenu(false);
  }, []);

  // Close sub menu
  const closeSubMenu = useCallback(() =>
  {
    if (corgeeSubMenuItemRef.current)
    {
      $(corgeeSubMenuItemRef.current).show();
    }
    setShowSubMenu(false);
  }, []);

  // On open menu
  const onOpenMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) =>
    {
      e.preventDefault();

      if (isOpen)
      {
        closeMenu();
        return;
      }

      const elem = e.currentTarget;
      const actionMenuElement = $(elem.closest('#actionMenuElement') || elem);

      const _canEdit = CorgeeHelper.replyHasEditPermissions(reply);
      const _canTemporary = CorgeeHelper.replyHasEditPermissionsTemporary(reply);
      const _canDelete = CorgeeHelper.replyHasDeletePermissions(reply);

      // Calculate initial position immediately from button rect (before API call)
      const buttonRect = elem.getBoundingClientRect();
      setTop(buttonRect.bottom);
      setLeft(buttonRect.left);
      setIsOpen(true);
      setCanEdit(_canEdit);
      setCanTemporary(_canTemporary);
      setCanDelete(_canDelete);

      // Calculate final position and reveal menu immediately (don't wait for API)
      setTimeout(() =>
      {
        const menuPosition = CorgeeHelper.getDropdownMenuPosition(actionMenuElement);
        setTop(menuPosition.top);
        setLeft(menuPosition.left);
        setIsPositioned(true);
      });

      MenuApi.getByConfigValuePerson(api as any, conversation.ProjectId)
        .then((response: MenuItem[]) =>
        {
          const filteredSubMenuItems = response.filter(
            x => x.ConfigValueID !== 1 && x.ConfigValueID !== 9 && x.ConfigValueID !== 15,
          );
          setSubMenuItems(filteredSubMenuItems);

          setTimeout(() =>
          {
            const groupContainer = actionMenuElement.closest('.dashboardGroupSelector')[0];
            if (groupContainer)
            {
              const initialScroll = $(groupContainer).scrollTop() as number;

              const watchScroll = (e: Event) =>
              {
                const finalScroll = $(groupContainer).scrollTop() as number;
                if (initialScroll !== finalScroll)
                {
                  setIsOpen(false);
                  e.currentTarget?.removeEventListener('scroll', watchScroll);
                }
              };

              groupContainer.addEventListener('scroll', watchScroll);
            }
          });
        })
        .catch(error =>
        {
          console.error(error);
        });
    },
    [api, conversation.ProjectId, reply, isOpen, closeMenu],
  );

  // On repost
  const onRepost = useCallback(
    (replyData: Reply) =>
    {
      if (onAddRepost)
      {
        onAddRepost(replyData);
      }
      closeMenu();
    },
    [onAddRepost, closeMenu],
  );

  // On mark as important
  const onMarkAsImportantHandler = useCallback(() =>
  {
    if (onMarkAsImportant)
    {
      onMarkAsImportant();
    }
    closeMenu();
  }, [onMarkAsImportant, closeMenu]);

  // Open Submit Time modal
  const openSubmitTime = useCallback(() =>
  {
    closeMenu();
    setSubmitTimeOpen(true);
  }, [closeMenu, onSubmitTime, onAddReply]);

  // On edit
  const onEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) =>
    {
      e.preventDefault();
      if (corgeeFunctions?.editReply)
      {
        corgeeFunctions.editReply(e, reply, conversation);
      }
      closeMenu();
    },
    [corgeeFunctions, reply, conversation, closeMenu],
  );

  // On delete
  const onDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) =>
    {
      e.preventDefault();

      if (corgeeFunctions?.loadingBarStart)
      {
        corgeeFunctions.loadingBarStart();
      }

      const replyWithSocket = {
        ...reply,
        SocketConnectionId,
        replyId: reply.ReplyId,
      };

      ReplyApi.deleteReply(api as any, replyWithSocket)
        .then(() =>
        {
          if (corgeeFunctions?.loadingBarComplete)
          {
            corgeeFunctions.loadingBarComplete();
          }

          if (reply.ReplyId)
          {
            MenuApi.deleteReplyTimeTrackings(api as any, reply.ReplyId)
              .then(() =>
              {
                conversationBus.emit('conversation-updated', {
                  conversationId: reply.ConversationId,
                  updates: { refreshCustomFields: true },
                });
              })
              .catch(error =>
              {
                console.error(error);
              });
          }

          if (onDeleteReply)
          {
            onDeleteReply(reply.ConversationId, reply.ReplyId, (totalReplies: number) =>
            {
              if (corgeeFunctions?.onReplyDeleted)
              {
                corgeeFunctions.onReplyDeleted(e, reply, totalReplies);
              }
            });
          }
        })
        .catch(error =>
        {
          console.error(error);
        });

      closeMenu();
    },
    [corgeeFunctions, reply, SocketConnectionId, api, onDeleteReply, closeMenu],
  );

  // Open sub menu
  const openSubMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) =>
  {
    const el = $(e.currentTarget);
    setShowSubMenu(true);

    setTimeout(() =>
    {
      if (!corgeeSubMenuItemRef.current) return;

      const containerElement = $(corgeeSubMenuItemRef.current);
      const containerMenu = el;

      const targetTopValue = containerMenu.offset()!.top - ($(window).scrollTop() as number);
      let leftPos = containerMenu.offset()!.left + containerMenu.width()! + 16;

      const objPosW =
        containerMenu.offset()!.left + containerMenu.width()! + containerElement.width()!;

      if (objPosW >= $(window).width()!)
      {
        leftPos = containerMenu.offset()!.left - containerElement.width()! - 3;
      }

      containerElement.attr('style', `top: ${targetTopValue}px; left: ${leftPos}px;`);
      containerElement.show();
    });
  }, []);

  // On create new item
  const onCreateNewItem = useCallback(
    (item: MenuItem) =>
    {
      if (item && corgeeFunctions?.onCreateItemFromCustomType)
      {
        corgeeFunctions.onCreateItemFromCustomType(item, conversation);
      }
      closeMenu();
      closeSubMenu();
    },
    [corgeeFunctions, conversation, closeMenu, closeSubMenu],
  );

  // Effect for component did mount
  useEffect(() =>
  {
    window.addEventListener('scroll', handleScroll);

    return () =>
    {
      window.removeEventListener('scroll', handleScroll);
      if (actionMenuElementRef.current)
      {
        $(actionMenuElementRef.current).off('hide.bs.dropdown');
      }
    };
  }, [handleScroll]);

  // Effect for resize window listener
  useEffect(() =>
  {
    if (closeOnResizeWindow && isOpen)
    {
      window.addEventListener('resize', onResizeWindow);
      return () =>
      {
        window.removeEventListener('resize', onResizeWindow);
      };
    }
    return undefined;
  }, [closeOnResizeWindow, isOpen, onResizeWindow]);

  // Computed values
  const menusReply = configMenus?.reply || [];
  const valueIsImportant = isImportant !== undefined ? isImportant : reply.IsImportant;

  return (
    <div>
      <div
        id="actionMenuElement"
        ref={actionMenuElementRef}
        tabIndex={0}
        className="corgee_com_card-action-menu-group dropdown"
      >
        <button
          className="corgee_com_card-action-menu-toggle dropdown-toggle"
          data-toggle="dropdown"
          onClick={onOpenMenu}
        >
          <div
            className="corgee_com_card-action-menu-icon"
            style={{ backgroundImage: `url(${threeDotsSvg})` }}
          ></div>
        </button>

        {isOpen &&
          createPortal(
            <ul
              className="corgee_com_card-action-header-menu dropdown-menu cancel-drag"
              style={{
                left,
                top,
                position: 'fixed',
                visibility: isPositioned ? 'visible' : 'hidden',
              }}
            >
              {(CorgeeHelper.inArray(menusReply, 'mark-important') || menusReply.length === 0) && (
                <li role="menuitem" className="corgee_com_card-action-menu-item">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={onMarkAsImportantHandler}
                  >
                    <i className="material-icons">priority_high</i>
                    <span>{valueIsImportant ? 'Unmark as Important' : 'Mark as Important'}</span>
                  </button>
                </li>
              )}

              {CorgeeHelper.inArray(menusReply, 'submit-time') && (
                <li role="menuitem" className="corgee_com_card-action-menu-item">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={openSubmitTime}
                  >
                    <i className="material-icons">schedule</i>
                    <span>Submit Time</span>
                  </button>
                </li>
              )}

              {(CorgeeHelper.inArray(menusReply, 'repost') || menusReply.length === 0) && (
                <li role="menuitem" className="corgee_com_card-action-menu-item">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={() => onRepost(reply)}
                  >
                    <i className="icon-refresh-icon"></i>
                    <span>Re-Post</span>
                  </button>
                </li>
              )}

              {(CorgeeHelper.inArray(menusReply, 'create-itemtype') || menusReply.length === 0) && (
                <li role="menuitem" className="corgee_com_card-action-menu-item dropdown-submenu">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={openSubMenu}
                  >
                    <i className="material-icons">add_circle</i>
                    <span>Create</span>
                  </button>

                  {showSubMenu &&
                    createPortal(
                      <div>
                        <ul
                          className="corgee_com_card-action-submenu dropdown-menu"
                          ref={corgeeSubMenuItemRef}
                          tabIndex={0}
                        >
                          {subMenuItems.map(item => (
                            <li
                              key={`create-config-value-id-${item.ConfigValueID}`}
                              role="menuitem"
                              className="corgee_com_card-action-menu-item create-item-menu"
                              data-config-value-id={item.ConfigValueID}
                              data-conversation-id={conversation.ConversationId}
                              data-reply-id="0"
                              data-config-value-text={item.Text}
                            >
                              <button
                                className="corgee_com_card-action-menu-item-button"
                                type="button"
                                onClick={() => onCreateNewItem(item)}
                              >
                                <i className={`material-icons ${item.Icon}`}></i>
                                <span>{item.Text}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>,
                      document.body,
                    )}
                </li>
              )}

              {(canEdit || canTemporary) &&
                (CorgeeHelper.inArray(menusReply, 'edit') || menusReply.length === 0) && (
                <li role="menuitem" className="corgee_com_card-action-menu-item">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={onEdit}
                  >
                    <i className="icon-pencil-icon"></i>
                    <span>Edit</span>
                  </button>
                </li>
              )}

              {canDelete &&
                (CorgeeHelper.inArray(menusReply, 'delete') || menusReply.length === 0) && (
                <li role="menuitem" className="corgee_com_card-action-menu-item">
                  <button
                    className="corgee_com_card-action-menu-item-button"
                    type="button"
                    onClick={onDelete}
                  >
                    <i className="icon-trash-icon"></i>
                    <span>Delete</span>
                  </button>
                </li>
              )}
            </ul>,
            document.body,
          )}
      </div>

      {isOpen &&
        createPortal(
          <div className="corgee_com_card-action-menu-backdrop" onClick={closeMenu}></div>,
          document.body,
        )}

      {submitTimeOpen && (
        <SubmitTime
          openmodal={submitTimeOpen}
          onclose={() => setSubmitTimeOpen(false)}
          conversation={conversation as any}
          api={api as any}
          typesubmittime={1}
          isedit={false}
          socketconnectionid={SocketConnectionId}
          onSubmitTime={onSubmitTime}
          onaddreply={onAddReply}
        />
      )}
    </div>
  );
};

export default ActionMenuReply;
